import React, {useState, useEffect} from 'react'
import {observer} from 'mobx-react-lite'
import {PreferenceToggle} from '../../pages/account/partials/account-preferences/preference-toggle'
import {Flex, Text, Box, Button} from '../../vanilla'
import {CheckIcon} from '../icons'
import {Modal} from '../modal'
import {
  useCustomerStore,
  useMarketingPreferencesStore,
  useGlobalStore,
  useAlertsStore
} from '../../store/hooks/useStore'
import {CommunicationPreferencesType, ChannelConsent} from '../../store/MarketingPreferencesStore'
import {useBannerTracking} from '../../analytics/banner-tracking'
import {flowResult} from 'mobx'
import {NotificationType} from '../../types/ui'
import {
  isMarketingReminderSuppressedForSession,
  suppressMarketingReminderForSession
} from '../../utils/marketing-preferences-reminder'
import {LOCALSTORAGE_KEYS} from '../../utils/constants'

export const MarketingPreferencesModal = observer(() => {
  const [modalOpen, setModalOpen] = useState(false)
  const [saveAttempted, setSaveAttempted] = useState(false)
  const [communicationPreferencesType, setCommunicationPreferencesType] =
    useState<CommunicationPreferencesType | null>(null)
  const [modalFrequency, setModalFrequency] = useState<number | null>(null)
  const {
    isLoadingCommunicationPreferences,
    communicationPreferences,
    fetchCommunicationPreferences,
    toggleCommunicationPreference,
    saveCommunicationPreferences,
    preferenceNeverSet,
    preferenceExpired,
    communicationPreferencesError
  } = useMarketingPreferencesStore()
  const {isCustomSitePreferenceSet, getCustomSitePreferenceById, customSitePreferences} =
    useGlobalStore()
  const {customerInfo, isRegistered} = useCustomerStore()
  const alertsStore = useAlertsStore()
  const [formattedTitle, setFormattedTitle] = useState("Don't miss out!")

  const hasUnsetPreferences = communicationPreferences.some((pref) => pref.value === 'Not Set')
  const allPreferencesUnset = communicationPreferences.every((pref) => pref.value === 'Not Set')

  const bannerTriggerStatus = preferenceNeverSet
    ? 'marketing_preference_not_set'
    : 'marketing_preference_expired'

  const sendBannerTrackingData = useBannerTracking({
    bannerId: 'preference_reminder_modal',
    bannerName: formattedTitle,
    bannerType: 'account_notice',
    bannerSlot: 'center_modal_popup',
    bannerFormat: 'alert_modal',
    bannerTriggerStatus
  })

  const dismissModal = () => {
    localStorage.setItem(LOCALSTORAGE_KEYS.MARKETING_MODAL_DISMISSED_KEY, Date.now().toString())
    setModalOpen(false)
  }

  const handleClose = () => {
    sendBannerTrackingData('alert_banner_dismiss', 'close_icon', 'dismissed')
    dismissModal()
  }

  const handleScreenTap = () => {
    sendBannerTrackingData('alert_banner_dismiss', 'screen_tap', 'dismissed')
    dismissModal()
  }

  const handleDismiss = () => {
    sendBannerTrackingData('alert_banner_dismiss', 'Remind me later', 'dismissed')
    dismissModal()
  }

  const handleSavePreferences = async () => {
    setSaveAttempted(true)
    if (hasUnsetPreferences) {
      return
    }

    if (communicationPreferencesType === CommunicationPreferencesType.CHANNEL_PREFERENCE) {
      suppressMarketingReminderForSession()
    }

    try {
      await flowResult(saveCommunicationPreferences(communicationPreferences))
      sendBannerTrackingData('alert_banner_click', 'Save my preferences', 'confirmed')
      alertsStore?.triggerNotification({
        message: 'Preferences Saved Successfully',
        type: NotificationType.success,
        isClosable: true,
        autoClose: true,
        autoCloseTimeMs: 5000
      })
    } catch (error) {
      alertsStore?.triggerNotification({
        message: 'Failed to Save Preferences',
        type: NotificationType.error,
        isClosable: true,
        autoClose: true,
        autoCloseTimeMs: 5000
      })
      console.error('Error saving preferences from modal:', error)
    } finally {
      setModalOpen(false)
    }
  }

  useEffect(() => {
    if (modalOpen) {
      sendBannerTrackingData('alert_banner_view')
    }
  }, [modalOpen])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('communicationPreferencesType')) {
        await flowResult(getCustomSitePreferenceById('communicationPreferencesType'))
      }

      if (!isCustomSitePreferenceSet('communicationPreferencesModalFrequency')) {
        await flowResult(getCustomSitePreferenceById('communicationPreferencesModalFrequency'))
      }

      setCommunicationPreferencesType(
        customSitePreferences['communicationPreferencesType'] as CommunicationPreferencesType
      )
      setModalFrequency(customSitePreferences['communicationPreferencesModalFrequency'] as number)
    })()
  }, [])

  // Get the Preferences if the user is registered and the preference type is set to channel preference
  useEffect(() => {
    if (
      isRegistered &&
      communicationPreferencesType === CommunicationPreferencesType.CHANNEL_PREFERENCE
    ) {
      fetchCommunicationPreferences()
    }
  }, [isRegistered, communicationPreferencesType])

  // If the user is registered, the preference type is channel preference and any of the preferences are not set, show the modal
  useEffect(() => {
    if (modalFrequency === null) {
      return
    }

    if (isMarketingReminderSuppressedForSession()) {
      setModalOpen(false)
      return
    }

    const dismissedAt =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(LOCALSTORAGE_KEYS.MARKETING_MODAL_DISMISSED_KEY)
        : null
    const hasModalBeenDismissed =
      dismissedAt !== null &&
      Date.now() - parseInt(dismissedAt, 10) < modalFrequency * 24 * 60 * 60 * 1000

    if (
      isRegistered &&
      !hasModalBeenDismissed &&
      !communicationPreferencesError &&
      !isLoadingCommunicationPreferences &&
      communicationPreferencesType === CommunicationPreferencesType.CHANNEL_PREFERENCE &&
      (preferenceNeverSet || preferenceExpired)
    ) {
      setModalOpen(true)
    }
  }, [
    isRegistered,
    communicationPreferencesError,
    isLoadingCommunicationPreferences,
    communicationPreferencesType,
    preferenceNeverSet,
    preferenceExpired,
    modalFrequency
  ])

  useEffect(() => {
    if (preferenceNeverSet) {
      setFormattedTitle(
        customerInfo?.firstName ? `Don't miss out, ${customerInfo.firstName}!` : "Don't miss out!"
      )
    } else if (
      preferenceExpired &&
      communicationPreferences.some((pref) => pref.value === 'Opt Out')
    ) {
      setFormattedTitle(
        customerInfo?.firstName
          ? `${customerInfo.firstName}, you're missing out!`
          : "You're missing out!"
      )
    } else if (
      preferenceExpired &&
      communicationPreferences.every((pref) => pref.value === 'Opt In')
    ) {
      setFormattedTitle(
        customerInfo?.firstName
          ? `Your preferences have expired, ${customerInfo.firstName}!`
          : 'Your preferences have expired!'
      )
    }
  }, [
    customerInfo?.firstName,
    preferenceNeverSet,
    preferenceExpired,
    communicationPreferences.length
  ])

  return (
    <Modal
      title={formattedTitle}
      size="sm"
      dialogContentProps={{
        position: 'relative',
        maxWidth: '320px'
      }}
      isOpen={modalOpen}
      onClose={handleScreenTap}
      onCloseButton={handleClose}
      data-test-selector="marketing-preferences-modal"
    >
      <Flex
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="16px"
        padding="20px"
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          padding="0px"
          gap="10px"
          maxWidth="320px"
          data-test-selector="update-info-box"
        >
          <Text lineHeight="none" variant="text3" data-test-selector="preferences-update-info-text">
            We&apos;ll contact you about:
          </Text>
          <Flex flexDirection="column" gap="4px" alignItems="flex-start">
            <Flex flexDirection="row" gap="10px" alignItems="center">
              <CheckIcon color="success" style={{width: 12, height: 'auto'}} />
              <Text
                lineHeight="none"
                variant="text3"
                data-test-selector="preferences-exclusive-offers-text"
              >
                Exclusive offers for online and in store
              </Text>
            </Flex>
            <Flex flexDirection="row" gap="10px" alignItems="center">
              <CheckIcon color="success" style={{width: 12, height: 'auto'}} />
              <Text
                lineHeight="none"
                variant="text3"
                data-test-selector="preferences-competitions-text"
              >
                Competitions and prize draws
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Text lineHeight="shorter" variant="text3" data-test-selector="preferences-change-info">
          You can change your preferences at any time within My Account
        </Text>
        <Box padding="12px" background="gray50" width="full" borderRadius="base">
          <Flex
            flexDirection="column"
            alignItems="stretch"
            gap="10px"
            data-test-selector="preferences-toggle-container"
            style={{width: '240px'}}
          >
            <Flex justifyContent="flex-end" gap="20px" paddingRight="2px">
              <Text variant="text3" data-test-selector="preferences-yes-text">
                Yes
              </Text>
              <Text variant="text3" data-test-selector="preferences-no-text">
                No
              </Text>
            </Flex>
            {communicationPreferences.map((option, index) => (
              <PreferenceToggle
                key={index}
                name={option.name}
                label={option.label}
                consent={option.value as ChannelConsent}
                onToggle={toggleCommunicationPreference}
                data-test-selector={`${option.name}-preference-toggle`}
              />
            ))}
          </Flex>
        </Box>
      </Flex>
      <Box padding="20px" borderTop="1px" borderColor="gray100">
        <Flex flexDirection="column" gap="8px">
          {saveAttempted && hasUnsetPreferences && (
            <Text variant="text3" color="error" data-test-selector="modal-save-error">
              Please select a preference for all channels before saving.
            </Text>
          )}
          <Button
            size="sm"
            variant="dark"
            disabled={allPreferencesUnset}
            onClick={handleSavePreferences}
            style={{width: '100%'}}
            data-test-selector="modal-save-preferences-button"
          >
            Save My Preferences
          </Button>
          <Button
            size="sm"
            variant="link"
            onClick={handleDismiss}
            style={{width: '100%'}}
            data-test-selector="modal-remind-later-button"
          >
            Remind me later
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
})
